import type { TypographyProps } from '@mui/material';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { MAX_DIRECT_UPLOAD_SIZE_MB } from '@constants/files';

import ChangeAvatar from '@views/ManageAccount/Profile/ChangeAvatar';

import type { GetOrganizationProfileQuery } from 'generated/account.generated';
import type { GetAccountSettingQuery } from 'generated/license.generated';

import BrandColorsForm from './BrandColorsForm';

const messages = defineMessages({
  brandTitle: {
    id: 'organization.setting.menu.brandSettingLogo',
    defaultMessage: 'Brand Icon',
  },
  brandSubtitle: {
    id: 'brandSettings.brandSubtitle',
    defaultMessage: 'Appears in the top-left corner of every page.',
  },
  dimensionsTitle: {
    id: 'brandSettings.dimensionsTitle',
    defaultMessage: 'Dimensions',
  },
  dimensionsValue: {
    id: 'brandSettings.dimensionsValue',
    defaultMessage: 'Minimum: 260 × 212 px',
  },
  dimensionsValue2: {
    id: 'brandSettings.dimensionsValue2',
    defaultMessage: 'Preferred: 520 × 424 px',
  },
  fileFormatTitle: {
    id: 'brandSettings.fileFormatTitle',
    defaultMessage: 'File Format',
  },
  fileFormatValue: {
    id: 'brandSettings.fileFormatValue',
    defaultMessage: 'SVG or PNG (Transparent background)',
  },
  fileSizeTitle: {
    id: 'brandImage.fileSizeTitle',
    defaultMessage: 'Max File Size',
  },
  fileSizeValue: {
    id: 'image.fileSizeValue',
    defaultMessage: '{limit} MB',
  },
  instructions: {
    id: 'organization.setting.colors',
    defaultMessage: 'Brand Colors',
  },
});
interface OrganizationProfileProps {
  account?: GetOrganizationProfileQuery['getAccountRecord'];
  accountSettings?: GetAccountSettingQuery;
  loading?: boolean;
}

export default function OrgBrandSettings({
  account,
  loading,
  accountSettings,
}: OrganizationProfileProps) {
  const { formatMessage } = useIntl();

  return (
    <Stack spacing={2}>
      <Card>
        <CardHeader
          title={formatMessage(messages.brandTitle)}
          subheader={formatMessage(messages.brandSubtitle)}
        />
        <CardContent>
          <ChangeAvatar
            account={{
              account_id: account?.account_id ?? '',
              account_name: account?.account_name,
              profile_picture: account?.profile_picture,
            }}
            accountSettings={accountSettings}
            logo={true}
            loading={loading ?? false}
          />
        </CardContent>
        <Divider />
      </Card>
      <Card>
        <CardHeader
          title={formatMessage(messages.instructions, {
            name: account?.account_name,
          })}
        />
        <CardContent>
          <BrandColorsForm accountSettings={accountSettings} />
        </CardContent>
      </Card>
    </Stack>
  );
}

export function BrandIconInfo() {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const bodyTypographyProps: TypographyProps = {
    variant: 'body2',
    color: 'text.secondary',
    ml: 1,
  };
  const subtitleTypographyProps: TypographyProps = {
    variant: 'subtitle2',
    color: 'text.primary',
  };
  return (
    <Box
      sx={{
        flex: 0.6,
        p: 2.5,
        pt: 0,
        mt: 0,
        borderRadius: 2,
        backgroundColor: theme.palette.grey[50],
        alignSelf: 'flex-start',
      }}
    >
      <Box sx={{ display: 'grid', gap: 0.1 }}>
        <Typography {...subtitleTypographyProps}>
          {formatMessage(messages.dimensionsTitle)}
        </Typography>
        <Typography {...bodyTypographyProps}>
          {formatMessage(messages.dimensionsValue)}
        </Typography>
        <Typography {...bodyTypographyProps}>
          {formatMessage(messages.dimensionsValue2)}
        </Typography>

        <Typography {...subtitleTypographyProps} sx={{ mt: 1 }}>
          {formatMessage(messages.fileFormatTitle)}
        </Typography>
        <Typography {...bodyTypographyProps}>
          {formatMessage(messages.fileFormatValue)}
        </Typography>

        <Typography {...subtitleTypographyProps} sx={{ mt: 1 }}>
          {formatMessage(messages.fileSizeTitle)}
        </Typography>
        <Typography {...bodyTypographyProps}>
          {formatMessage(messages.fileSizeValue, {
            limit: MAX_DIRECT_UPLOAD_SIZE_MB,
          })}
        </Typography>
      </Box>
    </Box>
  );
}
